import React, { useState, useEffect } from 'react';
import { auditService, Audit, AuditTemplate } from '@/firebase/auditService';
import { useNotification } from '@/contexts/NotificationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { isAdmin } from '@/constants/roles';
import { X, Calendar, MapPin, CheckCircle, Clock, Play, AlertCircle, ExternalLink, Trash2, ListChecks, FileText, BarChart3 } from 'lucide-react';
import { auditReadinessService } from '@/services/auditReadinessService';
import { getTasksForOrganization } from '@/services/taskService';

interface AuditHistoryModalProps {
  auditTemplate: AuditTemplate;
  onClose: () => void;
}

const AuditHistoryModal: React.FC<AuditHistoryModalProps> = ({ auditTemplate, onClose }) => {
  const { showNotification } = useNotification();
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const [audits, setAudits] = useState<Audit[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deletingAuditId, setDeletingAuditId] = useState<string | null>(null);
  const [auditToDelete, setAuditToDelete] = useState<Audit | null>(null);
  const [auditMetadata, setAuditMetadata] = useState<Record<string, {
    tasksCompleted: number;
    totalTasks: number;
    evidenceCount: number;
    readinessScore: number;
  }>>({});
  
  // Check if user is admin
  const userRole = user?.role || '';
  const isUserAdmin = isAdmin(userRole);

  useEffect(() => {
    if (currentOrganization?.id && auditTemplate.id) {
      loadAuditHistory();
    }
  }, [currentOrganization?.id, auditTemplate.id]);

  useEffect(() => {
    if (audits.length > 0) {
      loadAuditMetadata();
    }
  }, [audits]);

  const loadAuditHistory = async () => {
    if (!currentOrganization?.id) return;

    setIsLoading(true);
    try {
      const result = await auditService.getAuditsForOrganization(currentOrganization.id);
      if (result.success && result.data) {
        // Filter audits for this template
        const templateAudits = result.data.filter(
          audit => audit.auditTemplateId === auditTemplate.id
        );
        // Sort by scheduled date (most recent first)
        templateAudits.sort((a, b) => {
          const dateA = a.scheduledDate?.toMillis?.() || a.scheduledDate || 0;
          const dateB = b.scheduledDate?.toMillis?.() || b.scheduledDate || 0;
          return dateB - dateA;
        });
        setAudits(templateAudits);
      } else {
        showNotification(`Error loading audit history: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error loading audit history:', error);
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: Audit['status']) => {
    const statusConfig = {
      planned: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Planned' },
      in_progress: { color: 'bg-yellow-100 text-yellow-800', icon: Play, label: 'In Progress' },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
      overdue: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Overdue' },
      cancelled: { color: 'bg-gray-100 text-gray-800', icon: X, label: 'Cancelled' },
    };

    const config = statusConfig[status] || statusConfig.planned;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const loadAuditMetadata = async () => {
    const metadata: Record<string, {
      tasksCompleted: number;
      totalTasks: number;
      evidenceCount: number;
      readinessScore: number;
    }> = {};

    // Load metadata for all audits
    for (const audit of audits) {
      if (!audit.id) continue;

      try {
        // Calculate readiness score
        const readiness = await auditReadinessService.calculateReadiness(audit);
        
        // Get task counts (already filtered by organization on server side)
        const orgTasks = await getTasksForOrganization(audit.organizationId);
        const relevantTasks = orgTasks.filter(task => 
          task.locationId === audit.locationId &&
          (audit.complianceTemplateIds?.includes(task.templateId) || 
           audit.associatedTaskIds?.includes(task.id))
        );
        
        const tasksCompleted = relevantTasks.filter(task => task.status === 'completed').length;
        const totalTasks = relevantTasks.length;
        const evidenceCount = audit.evidence?.length || 0;

        metadata[audit.id] = {
          tasksCompleted,
          totalTasks,
          evidenceCount,
          readinessScore: readiness.overallScore
        };
      } catch (error) {
        console.error(`Error loading metadata for audit ${audit.id}:`, error);
        // Set default values on error
        metadata[audit.id] = {
          tasksCompleted: 0,
          totalTasks: 0,
          evidenceCount: audit.evidence?.length || 0,
          readinessScore: audit.completionPercentage || 0
        };
      }
    }

    setAuditMetadata(metadata);
  };

  const handleDeleteAudit = async (audit: Audit) => {
    if (!audit.id) return;

    setDeletingAuditId(audit.id);
    try {
      const userInfo = user ? { id: user.id, name: user.name || '', email: user.email || '' } : undefined;
      const result = await auditService.deleteAudit(audit.id, userInfo);

      if (result.success) {
        showNotification('Audit deleted successfully', 'success');
        // Remove from local state
        setAudits(prev => prev.filter(a => a.id !== audit.id));
        setAuditToDelete(null);
      } else {
        showNotification(`Error deleting audit: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error deleting audit:', error);
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setDeletingAuditId(null);
    }
  };

  return (
    <>
      {/* Main History Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-semibold text-gray-800">Audit History</h2>
                <p className="text-sm text-gray-600 mt-1">{auditTemplate.name}</p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading audit history...</p>
              </div>
            ) : audits.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-700 mb-2">No audit history</p>
                <p className="text-sm text-gray-500">
                  No audits have been created for this template yet.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {audits.map((audit) => (
                  <div
                    key={audit.id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-gray-900">{audit.locationName}</h3>
                          {getStatusBadge(audit.status)}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 mb-3">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span>Scheduled: {formatDate(audit.scheduledDate)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span>Due: {formatDate(audit.dueDate)}</span>
                          </div>
                          {audit.completedAt && (
                            <div className="flex items-center gap-1">
                              <CheckCircle className="w-4 h-4 text-gray-400" />
                              <span>Completed: {formatDate(audit.completedAt)}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <BarChart3 className="w-4 h-4 text-gray-400" />
                            <span>Readiness: {auditMetadata[audit.id || '']?.readinessScore ?? audit.completionPercentage ?? 0}%</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm text-gray-600 pt-3 border-t border-gray-100">
                          <div className="flex items-center gap-2">
                            <ListChecks className="w-4 h-4 text-gray-400" />
                            <span>
                              <span className="font-medium">Tasks:</span>{' '}
                              {auditMetadata[audit.id || '']?.tasksCompleted ?? 0} / {auditMetadata[audit.id || '']?.totalTasks ?? 0}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-gray-400" />
                            <span>
                              <span className="font-medium">Evidence:</span>{' '}
                              {auditMetadata[audit.id || '']?.evidenceCount ?? audit.evidence?.length ?? 0}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-gray-400" />
                            <span>
                              <span className="font-medium">Findings:</span>{' '}
                              {audit.findings?.length || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (audit.id) {
                              // Open in new tab
                              const url = `/audit/${audit.id}`;
                              window.open(url, '_blank');
                            }
                          }}
                          className="text-green-600 hover:text-green-900"
                          title="Open Full View (New Tab)"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                        {isUserAdmin && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setAuditToDelete(audit);
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Audit"
                            disabled={deletingAuditId === audit.id}
                          >
                            {deletingAuditId === audit.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {auditToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Audit</h3>
                <p className="text-sm text-gray-600">This action cannot be undone.</p>
              </div>
            </div>
            
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Location:</span> {auditToDelete.locationName}
              </p>
              <p className="text-sm text-gray-700 mt-1">
                <span className="font-medium">Scheduled:</span> {formatDate(auditToDelete.scheduledDate)}
              </p>
              <p className="text-sm text-gray-700 mt-1">
                <span className="font-medium">Status:</span> {auditToDelete.status}
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setAuditToDelete(null)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                disabled={deletingAuditId !== null}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteAudit(auditToDelete)}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={deletingAuditId !== null}
              >
                {deletingAuditId ? 'Deleting...' : 'Delete Audit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditHistoryModal;

